import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertTriangle, Download, FileQuestion, ZoomIn, ZoomOut, Maximize2, ExternalLink,
} from 'lucide-react';
import useAuthedBlob from '../components/useAuthedBlob';
import { sopApi, fetchText, downloadOriginal, formatBytes } from '../api';
import { renderMarkdown } from '../markdown';

// ---------------------------------------------------------------------------
// Shared shells
// ---------------------------------------------------------------------------
const Centered = ({ children }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-20 text-center px-6">{children}</div>
);

const Spinner = ({ label }) => (
  <Centered>
    <Loader2 className="w-7 h-7 animate-spin text-indigo-400" />
    <p className="text-sm text-slate-400">{label}</p>
  </Centered>
);

const LoadError = ({ message, doc }) => (
  <Centered>
    <AlertTriangle className="w-10 h-10 text-amber-400" />
    <p className="text-slate-300 font-medium">Couldn&rsquo;t display this file</p>
    <p className="text-sm text-slate-500 max-w-md">{message}</p>
    {doc && <DownloadButton doc={doc} />}
  </Centered>
);

const DownloadButton = ({ doc, label = 'Download original' }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try { await downloadOriginal(doc.id, null, doc.originalName); } finally { setBusy(false); }
      }}
      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
        text-white rounded-md text-sm font-medium transition disabled:opacity-60"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      {label}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Per-kind renderers
// ---------------------------------------------------------------------------

/**
 * Office documents are converted to PDF on ingest, so this one renderer covers
 * Word, PowerPoint, Excel, PDFs and anything else LibreOffice can read — which
 * is what makes every SOP look the same regardless of what was uploaded.
 * The browser's built-in PDF viewer handles paging, zoom, search and print.
 */
const PdfRenderer = ({ doc, versionId }) => {
  const { url, loading, error } = useAuthedBlob(sopApi.fileUrl(doc.id, versionId));

  if (loading) return <Spinner label="Loading document…" />;
  if (error) return <LoadError message={error} doc={doc} />;

  return (
    <iframe
      src={`${url}#view=FitH`}
      title={doc.title}
      className="w-full h-full min-h-[70vh] bg-slate-950 rounded-lg border border-slate-700"
    />
  );
};

const ImageRenderer = ({ doc, versionId }) => {
  const { url, loading, error } = useAuthedBlob(sopApi.fileUrl(doc.id, versionId));
  const [zoom, setZoom] = useState(1);

  if (loading) return <Spinner label="Loading image…" />;
  if (error) return <LoadError message={error} doc={doc} />;

  return (
    <div className="relative bg-slate-950 rounded-lg border border-slate-700 overflow-auto min-h-[70vh] flex items-center justify-center">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-slate-800/90 border border-slate-700 rounded-md p-1">
        <button type="button" onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
          className="p-1.5 rounded text-slate-300 hover:bg-slate-700" aria-label="Zoom out">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-400 w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => setZoom((z) => Math.min(5, z + 0.25))}
          className="p-1.5 rounded text-slate-300 hover:bg-slate-700" aria-label="Zoom in">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => setZoom(1)}
          className="p-1.5 rounded text-slate-300 hover:bg-slate-700" aria-label="Reset zoom">
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>
      <img
        src={url}
        alt={doc.title}
        style={{ transform: `scale(${zoom})` }}
        className="max-w-full transition-transform origin-center"
      />
    </div>
  );
};

const MediaRenderer = ({ doc, versionId, kind }) => {
  const { url, loading, error } = useAuthedBlob(sopApi.fileUrl(doc.id, versionId));

  if (loading) return <Spinner label={`Loading ${kind}…`} />;
  if (error) return <LoadError message={error} doc={doc} />;

  return (
    <div className="bg-slate-950 rounded-lg border border-slate-700 p-4 flex items-center justify-center min-h-[40vh]">
      {kind === 'video'
        ? <video src={url} controls className="max-w-full max-h-[70vh] rounded-md">
            <track kind="captions" />
          </video>
        : <audio src={url} controls className="w-full max-w-xl" />}
    </div>
  );
};

/** Fetches text content once and hands it to a child renderer. */
const useTextContent = (doc, versionId) => {
  const [state, setState] = useState({ text: '', loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ text: '', loading: true, error: null });
    fetchText(sopApi.fileUrl(doc.id, versionId))
      .then((text) => { if (!cancelled) setState({ text, loading: false, error: null }); })
      .catch((err) => { if (!cancelled) setState({ text: '', loading: false, error: err.message }); });
    return () => { cancelled = true; };
  }, [doc.id, versionId]);

  return state;
};

const TextRenderer = ({ doc, versionId }) => {
  const { text, loading, error } = useTextContent(doc, versionId);
  if (loading) return <Spinner label="Loading file…" />;
  if (error) return <LoadError message={error} doc={doc} />;
  return (
    <pre className="bg-slate-950 rounded-lg border border-slate-700 p-5 overflow-auto min-h-[50vh]
      text-sm font-mono text-slate-200 whitespace-pre-wrap break-words">
      {text}
    </pre>
  );
};

const MarkdownFileRenderer = ({ doc, versionId }) => {
  const { text, loading, error } = useTextContent(doc, versionId);
  if (loading) return <Spinner label="Loading document…" />;
  if (error) return <LoadError message={error} doc={doc} />;
  return <MarkdownBody source={text} />;
};

/** Shared by uploaded .md files and SOPs written in the HQ editor. */
export const MarkdownBody = ({ source }) => (
  <div
    className="bg-slate-950 rounded-lg border border-slate-700 p-6 md:p-8 min-h-[50vh] max-w-4xl mx-auto"
    // Safe: renderMarkdown escapes the entire source before emitting any tag,
    // and every tag in its output is one it constructs itself.
    dangerouslySetInnerHTML={{ __html: renderMarkdown(source) }}
  />
);

/** Minimal RFC-4180-ish parser: handles quoted fields and embedded separators. */
const parseDelimited = (text, delimiter) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; } else inQuotes = false;
      } else field += char;
      continue;
    }
    if (char === '"') { inQuotes = true; continue; }
    if (char === delimiter) { row.push(field); field = ''; continue; }
    if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field); field = '';
      rows.push(row); row = [];
      continue;
    }
    field += char;
  }
  if (field || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c !== ''));
};

const MAX_CSV_ROWS = 500;

const CsvRenderer = ({ doc, versionId }) => {
  const { text, loading, error } = useTextContent(doc, versionId);
  const delimiter = (doc.originalExt || '').toLowerCase() === 'tsv' ? '\t' : ',';
  const rows = useMemo(() => (text ? parseDelimited(text, delimiter) : []), [text, delimiter]);

  if (loading) return <Spinner label="Loading spreadsheet…" />;
  if (error) return <LoadError message={error} doc={doc} />;
  if (!rows.length) return <LoadError message="This file appears to be empty." doc={doc} />;

  const [header, ...body] = rows;
  const shown = body.slice(0, MAX_CSV_ROWS);

  return (
    <div className="bg-slate-950 rounded-lg border border-slate-700 overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-800 sticky top-0">
            <tr>
              {header.map((cell, i) => (
                <th key={i} className="px-3 py-2 text-left font-semibold text-slate-200 border-b border-slate-700 whitespace-nowrap">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, ri) => (
              <tr key={ri} className="border-b border-slate-800 hover:bg-slate-800/40">
                {header.map((_, ci) => (
                  <td key={ci} className="px-3 py-2 text-slate-300 align-top">{r[ci] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {body.length > MAX_CSV_ROWS && (
        <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-700 bg-slate-900">
          Showing first {MAX_CSV_ROWS} of {body.length} rows — download the file for the rest.
        </div>
      )}
    </div>
  );
};

/**
 * Uploaded HTML renders inside a fully sandboxed iframe: no allow-scripts and
 * no allow-same-origin, so the document lands in an opaque origin and cannot
 * reach the session, cookies, or the parent page. srcDoc (rather than a blob
 * URL) keeps it from ever being a navigable same-origin document.
 */
const HtmlRenderer = ({ doc, versionId }) => {
  const { text, loading, error } = useTextContent(doc, versionId);
  if (loading) return <Spinner label="Loading page…" />;
  if (error) return <LoadError message={error} doc={doc} />;
  return (
    <iframe
      title={doc.title}
      srcDoc={text}
      sandbox=""
      className="w-full min-h-[70vh] bg-white rounded-lg border border-slate-700"
    />
  );
};

const LinkRenderer = ({ doc }) => (
  <Centered>
    <ExternalLink className="w-10 h-10 text-indigo-400" />
    <p className="text-slate-300 font-medium">This SOP lives outside the HQ</p>
    <a
      href={doc.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
        text-white rounded-md text-sm font-medium transition"
    >
      <ExternalLink className="w-4 h-4" />
      Open link
    </a>
    <p className="text-xs text-slate-500 break-all max-w-lg">{doc.externalUrl}</p>
  </Centered>
);

/**
 * Archives and formats nothing on the box can render. Deliberately not an
 * error: the SOP is intact and downloadable, it just has no preview.
 */
const DownloadOnlyRenderer = ({ doc }) => (
  <Centered>
    <FileQuestion className="w-10 h-10 text-slate-500" />
    <div>
      <p className="text-slate-300 font-medium">No preview for this format</p>
      <p className="text-sm text-slate-500 mt-1">
        {doc.originalName}
        {doc.sizeBytes ? ` · ${formatBytes(doc.sizeBytes)}` : ''}
      </p>
    </div>
    <DownloadButton doc={doc} />
  </Centered>
);

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------
/**
 * Chooses a renderer from the version's render_kind, which the server assigned
 * during ingest. The client never sniffs file types itself — by the time a
 * document reaches here it has already been normalized.
 */
const UniversalViewer = ({ doc, versionId = null }) => {
  if (doc.docKind === 'link') return <LinkRenderer doc={doc} />;

  if (doc.docKind === 'native') {
    return <MarkdownBody source={doc.bodyMarkdown || ''} />;
  }

  if (!doc.currentVersionId) {
    return <LoadError message="This SOP has no file attached yet." />;
  }

  // Conversion runs in the background; the viewer polls via the parent and
  // re-renders when the status settles.
  if (doc.renderStatus === 'pending' || doc.renderStatus === 'processing') {
    return <Spinner label="Preparing this document for viewing…" />;
  }

  if (doc.renderStatus === 'failed') {
    return (
      <LoadError
        message="We couldn't convert this file for preview, but the original is intact and downloadable."
        doc={doc}
      />
    );
  }

  switch (doc.renderKind) {
    case 'pdf': return <PdfRenderer doc={doc} versionId={versionId} />;
    case 'image': return <ImageRenderer doc={doc} versionId={versionId} />;
    case 'video': return <MediaRenderer doc={doc} versionId={versionId} kind="video" />;
    case 'audio': return <MediaRenderer doc={doc} versionId={versionId} kind="audio" />;
    case 'markdown': return <MarkdownFileRenderer doc={doc} versionId={versionId} />;
    case 'csv': return <CsvRenderer doc={doc} versionId={versionId} />;
    case 'html': return <HtmlRenderer doc={doc} versionId={versionId} />;
    case 'text': return <TextRenderer doc={doc} versionId={versionId} />;
    case 'native': return <MarkdownBody source={doc.bodyMarkdown || ''} />;
    default: return <DownloadOnlyRenderer doc={doc} />;
  }
};

export default UniversalViewer;
